import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Check, Sparkles, ShieldCheck, Crown, Rocket } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);

  const reloadMe = () => {
    if (user) api.get("/billing/me").then(({ data }) => setMe(data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => {
      setPlans(data.plans);
      setAddons(data.addons || []);
    });
    reloadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const startTrial = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setTrialBusy(true);
    try {
      await api.post("/billing/trial");
      toast.success(t("pricing.trialActivated"));
      reloadMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setTrialBusy(false);
    }
  };

  const handleSelect = async (planId) => {
    if (planId === "free") {
      navigate(user ? "/dashboard" : "/login");
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setBusy(null);
    }
  };

  const isCurrent = (planId) => me?.plan === planId;

  const icons = { free: ShieldCheck, pro: Sparkles, unlimited: Crown };

  return (
    <div className="App min-h-screen bg-background">
      <Header variant={user ? "app" : "marketing"} />
      <main className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="text-center">
            <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {t("pricing.tagline")}</div>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-6xl">
              {t("pricing.title")}
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-muted-foreground">{t("pricing.subtitle")}</p>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {plans.map((p) => {
              const Icon = icons[p.id] || ShieldCheck;
              const popular = p.id === "pro";
              const features = lang === "fr" ? p.features_fr : p.features_en;
              const current = isCurrent(p.id);
              return (
                <div
                  key={p.id}
                  className={`relative flex flex-col border bg-card p-8 transition ${
                    popular ? "border-[#002FA7] shadow-[0_0_0_2px_#002FA7]" : "border-border hover:border-foreground/30"
                  }`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {popular && (
                    <div className="absolute -top-3 left-8 bg-[#002FA7] px-2 py-0.5 font-mono text-[10px] tracking-wider text-white">
                      {t("pricing.mostPopular")}
                    </div>
                  )}
                  {current && (
                    <div className="absolute -top-3 right-8 bg-emerald-600 px-2 py-0.5 font-mono text-[10px] tracking-wider text-white">
                      {t("pricing.currentPlan").toUpperCase()}
                    </div>
                  )}

                  <div className="flex h-12 w-12 items-center justify-center border border-border bg-background">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h2 className="mt-6 font-display text-3xl font-black tracking-tight">{p.label}</h2>

                  <div className="mt-3 flex items-baseline gap-2">
                    {p.price_eur === 0 ? (
                      <span className="font-display text-5xl font-black tracking-tight">{t("pricing.free")}</span>
                    ) : (
                      <>
                        <span className="font-display text-5xl font-black tracking-tight">{Math.round(p.price_eur)}€</span>
                        <span className="font-mono text-sm text-muted-foreground">{t("pricing.perMonth")}</span>
                      </>
                    )}
                  </div>

                  <div className="mt-2 font-mono text-xs uppercase text-muted-foreground">
                    {p.unlimited
                      ? t("pricing.unlimited")
                      : `${p.quota_gb} ${lang === "fr" ? "Go / mois" : "GB / month"}`}
                  </div>

                  <ul className="mt-8 space-y-3">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#002FA7]" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex-1" />

                  <Button
                    onClick={() => handleSelect(p.id)}
                    disabled={busy === p.id || current}
                    className={`mt-2 h-12 w-full rounded-sm font-mono uppercase tracking-wider text-xs ${
                      popular
                        ? "bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
                        : current
                        ? "bg-muted text-muted-foreground"
                        : "bg-foreground text-background hover:bg-foreground/90"
                    }`}
                    data-testid={`plan-cta-${p.id}`}
                  >
                    {current
                      ? t("pricing.ctaCurrent")
                      : busy === p.id
                      ? t("pricing.upgrading")
                      : p.price_eur === 0
                      ? t("pricing.ctaFree")
                      : t("pricing.cta")}
                  </Button>

                  {/* Trial CTA — only on Pro card */}
                  {p.id === "pro" && !current && me && (
                    <div className="mt-3 border border-dashed border-[#002FA7]/40 bg-[#002FA7]/5 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-[#002FA7]">
                          {t("pricing.trialBadge")}
                        </div>
                        <div className="font-mono text-[9px] uppercase text-muted-foreground">
                          {t("pricing.noCreditCard")}
                        </div>
                      </div>
                      <button
                        onClick={startTrial}
                        disabled={trialBusy || me.trial_used}
                        className="mt-2 w-full bg-transparent text-left font-display text-sm font-bold tracking-tight text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid="trial-cta-button"
                      >
                        →&nbsp;{me.trial_used ? t("pricing.trialUsed") : t("pricing.tryPro")}
                      </button>
                    </div>
                  )}
                  {p.id === "pro" && !user && (
                    <div className="mt-3 border border-dashed border-[#002FA7]/40 bg-[#002FA7]/5 p-3">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-[#002FA7]">
                        {t("pricing.trialBadge")} · {t("pricing.noCreditCard")}
                      </div>
                      <button
                        onClick={() => navigate("/login")}
                        className="mt-2 w-full bg-transparent text-left font-display text-sm font-bold tracking-tight text-foreground hover:underline"
                        data-testid="trial-cta-anon"
                      >
                        →&nbsp;{t("pricing.tryPro")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center font-mono text-[11px] text-muted-foreground">
            VAT included where applicable · Stripe test mode · No card stored — managed by Stripe
          </p>

          {/* Migration add-ons */}
          {addons.length > 0 && (
            <section className="mt-24 border-t border-border pt-16">
              <div className="text-center">
                <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// MIGRATION</div>
                <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
                  {t("pricing.addonsTitle")}
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t("pricing.addonsSubtitle")}</p>
              </div>
              <div className="mt-10 mx-auto max-w-3xl">
                {addons.map((a) => {
                  const features = lang === "fr" ? a.features_fr : a.features_en;
                  return (
                    <div key={a.id} className="relative border-2 border-foreground bg-card p-8">
                      <div className="absolute -top-3 left-8 bg-foreground px-2 py-0.5 font-mono text-[10px] tracking-wider text-background">
                        {t("pricing.oneShot").toUpperCase()}
                      </div>
                      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
                        <div>
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center bg-[#002FA7] text-white">
                              <Rocket className="h-5 w-5" />
                            </div>
                            <div>
                              <h3 className="font-display text-3xl font-black tracking-tight">{a.label}</h3>
                              <div className="font-mono text-xs uppercase text-muted-foreground">
                                {t("pricing.migrationDuration")} · {t("pricing.unlimited")}
                              </div>
                            </div>
                          </div>
                          <div className="mt-6 flex items-baseline gap-2">
                            <span className="font-display text-5xl font-black tracking-tight">{Math.round(a.price_eur)}€</span>
                            <span className="font-mono text-sm text-muted-foreground">· {t("pricing.oneShot").toLowerCase()}</span>
                          </div>
                          <Button
                            onClick={() => handleSelect(a.id)}
                            disabled={busy === a.id}
                            className="mt-6 h-12 w-full sm:w-auto rounded-sm bg-foreground text-background hover:bg-foreground/90 font-mono uppercase tracking-wider text-xs px-6"
                            data-testid="migration-cta-button"
                          >
                            {busy === a.id ? t("pricing.upgrading") : t("pricing.migrationCta")}
                          </Button>
                        </div>
                        <ul className="space-y-3">
                          {features.map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#002FA7]" />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
      {!user && <Footer />}
    </div>
  );
}
